import type { Body } from './Body';
import { normalizeAngle } from './Body';

export type PhysicsTuning = {
  linearDamping: number;
  angularDamping: number;
  restitution: number;
  maxSpeed: number;
  maxAngular: number;
};

export type Bounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export class PhysicsWorld {
  readonly fixedDt = 1 / 60;
  private accumulator = 0;
  private readonly maxFrame = 0.05;
  stepsLastFrame = 0;

  constructor(public tuning: PhysicsTuning) {}

  step(dt: number, bodies: Body[], bounds: Bounds): void {
    this.accumulator += Math.min(dt, this.maxFrame);
    this.stepsLastFrame = 0;
    while (this.accumulator >= this.fixedDt) {
      this.integrate(bodies, this.fixedDt, bounds);
      this.accumulator -= this.fixedDt;
      this.stepsLastFrame += 1;
    }
  }

  private integrate(bodies: Body[], dt: number, bounds: Bounds): void {
    const { linearDamping, angularDamping, maxSpeed, maxAngular } = this.tuning;
    for (const b of bodies) {
      b.vx *= Math.exp(-linearDamping * dt);
      b.vy *= Math.exp(-linearDamping * dt);
      b.angularVelocity *= Math.exp(-angularDamping * dt);

      const speed = Math.hypot(b.vx, b.vy);
      if (speed > maxSpeed) {
        const s = maxSpeed / speed;
        b.vx *= s;
        b.vy *= s;
      }
      b.angularVelocity = Math.max(-maxAngular, Math.min(maxAngular, b.angularVelocity));

      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.angle = normalizeAngle(b.angle + b.angularVelocity * dt);

      this.collideBounds(b, bounds);
    }
  }

  private collideBounds(b: Body, bounds: Bounds): void {
    const hw = b.width * 0.5;
    const hh = b.height * 0.5;
    const minX = bounds.x + hw;
    const maxX = bounds.x + bounds.width - hw;
    const minY = bounds.y + hh;
    const maxY = bounds.y + bounds.height - hh;
    const rest = b.restitution;

    if (b.x < minX) {
      b.x = minX;
      b.vx = Math.abs(b.vx) * rest;
      b.angularVelocity *= 0.85;
    } else if (b.x > maxX) {
      b.x = maxX;
      b.vx = -Math.abs(b.vx) * rest;
      b.angularVelocity *= 0.85;
    }

    if (b.y < minY) {
      b.y = minY;
      b.vy = Math.abs(b.vy) * rest;
      b.angularVelocity *= 0.85;
    } else if (b.y > maxY) {
      b.y = maxY;
      b.vy = -Math.abs(b.vy) * rest;
      b.angularVelocity *= 0.85;
    }
  }
}
